import { Injectable, inject } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { DownloadPresignedurlService } from '../doc-module-service/download-presignedurl-service/download-presignedurl.service';
import { LoggerService } from '../logger-services/logger.service';
import { FilePreviewRequest, FilePreviewData } from '../../app/models/file-preview.models';

@Injectable({
  providedIn: 'root'
})
export class FilePreviewService {
  private readonly http = inject(HttpClient);
  private readonly downloadPresignedUrlService = inject(DownloadPresignedurlService);
  private readonly loggerService = inject(LoggerService);

  /**
   * Get file preview data based on request type
   * Supports three sources: documentId (from myfiles), presignedUrl (from API), or direct File object
   */
  getFilePreview(request: FilePreviewRequest): Observable<FilePreviewData> {
    try {
      const fileType = this.getFileType(request.fileName);
      
      // Route to appropriate method based on source
      if (request.file) {
        return this.previewFromFile(request.file, fileType);
      } else if (request.presignedUrl) {
        return this.previewFromPresignedUrl(request.presignedUrl, request.fileName, fileType);
      } else if (request.documentId) {
        return this.previewFromDocumentId(request.documentId, request.fileName, fileType);
      } else {
        return throwError(() => new Error('No valid file source provided'));
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'getFilePreview');
      return throwError(() => error);
    }
  }

  /**
   * Preview file from documentId (myfiles scenario)
   */
  private previewFromDocumentId(documentId: number, fileName: string, fileType: 'pdf' | 'docx' | 'txt' | 'unknown'): Observable<FilePreviewData> {
    return this.downloadPresignedUrlService.getPresignedDownloadUrl(documentId).pipe(
      switchMap(response => {
        if (response.code === 200 && response.data?.presignedUrl) {
          return this.previewFromPresignedUrl(response.data.presignedUrl, fileName, fileType);
        }
        throw new Error('Failed to get presigned URL');
      }),
      catchError(error => {
        this.loggerService.logError(this.formatError(error), 'previewFromDocumentId');
        return of({
          type: fileType,
          fileName: fileName,
          fileSize: 0,
          error: 'Failed to load file preview'
        });
      })
    );
  }

  /**
   * Preview file from presigned URL
   * Uses XMLHttpRequest directly to avoid Angular's interceptors adding headers
   * Similar to the file upload service approach
   */
  private previewFromPresignedUrl(presignedUrl: string, fileName: string, fileType: 'pdf' | 'docx' | 'txt' | 'unknown'): Observable<FilePreviewData> {
    return new Observable<FilePreviewData>(observer => {
      const xhr = new XMLHttpRequest();
      
      xhr.responseType = 'blob';
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = xhr.response as Blob;
          
          // Process the blob and emit result
          this.processBlob(blob, fileName, fileType).subscribe({
            next: (data) => {
              observer.next(data);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
            }
          });
        } else {
          observer.next({
            type: fileType,
            fileName: fileName,
            fileSize: 0,
            error: `HTTP error! status: ${xhr.status}`
          });
          observer.complete();
        }
      });

      xhr.addEventListener('error', () => {
        this.loggerService.logError(
          this.formatError({ message: 'Network error', status: xhr.status }),
          'previewFromPresignedUrl'
        );
        observer.next({
          type: fileType,
          fileName: fileName,
          fileSize: 0,
          error: 'Network error while downloading file'
        });
        observer.complete();
      });

      xhr.addEventListener('abort', () => {
        observer.next({
          type: fileType,
          fileName: fileName,
          fileSize: 0,
          error: 'Download was aborted'
        });
        observer.complete();
      });

      // Open and send the request - no custom headers
      xhr.open('GET', presignedUrl, true);
      xhr.send();
    });
  }

  /**
   * Preview from File object (direct upload scenario)
   */
  private previewFromFile(file: File, fileType: 'pdf' | 'docx' | 'txt' | 'unknown'): Observable<FilePreviewData> {
    return this.processBlob(file, file.name, fileType);
  }

  /**
   * Process blob based on file type
   */
  private processBlob(blob: Blob, fileName: string, fileType: 'pdf' | 'docx' | 'txt' | 'unknown'): Observable<FilePreviewData> {
    try {
      switch (fileType) {
        case 'pdf':
          return this.processPdf(blob, fileName);
        case 'docx':
          return this.processDocx(blob, fileName);
        case 'txt':
          return this.processTxt(blob, fileName);
        default:
          return of({
            type: 'unknown',
            fileName: fileName,
            fileSize: blob.size,
            error: 'Unsupported file type'
          });
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'processBlob');
      return of({
        type: fileType,
        fileName: fileName,
        fileSize: blob.size,
        error: 'Failed to process file'
      });
    }
  }

  /**
   * Process PDF file - create blob URL for pdf-viewer
   */
  private processPdf(blob: Blob, fileName: string): Observable<FilePreviewData> {
    try {
      const pdfBlob = blob.type === 'application/pdf'
        ? blob
        : new Blob([blob], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      return of({
        type: 'pdf',
        blobUrl: blobUrl,
        fileName: fileName,
        fileSize: blob.size
      });
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'processPdf');
      return of({
        type: 'pdf',
        fileName: fileName,
        fileSize: blob.size,
        error: 'Failed to process PDF file'
      });
    }
  }

  /**
   * Process DOCX file - create blob URL for docx-preview library
   */
  private processDocx(blob: Blob, fileName: string): Observable<FilePreviewData> {
    try {
      // Create blob URL for docx-preview to render
      const blobUrl = URL.createObjectURL(blob);
      
      return of({
        type: 'docx' as const,
        blobUrl: blobUrl,
        fileName: fileName,
        fileSize: blob.size
      });
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'processDocx');
      return of({
        type: 'docx' as const,
        fileName: fileName,
        fileSize: blob.size,
        error: 'Failed to process DOCX file'
      });
    }
  }

  /**
   * Process TXT file - read as text
   */
  private processTxt(blob: Blob, fileName: string): Observable<FilePreviewData> {
    return from(
      blob.text().then(text => {
        return {
          type: 'txt' as const,
          content: text,
          fileName: fileName,
          fileSize: blob.size
        };
      }).catch(error => {
        this.loggerService.logError(this.formatError(error), 'processTxt');
        return {
          type: 'txt' as const,
          fileName: fileName,
          fileSize: blob.size,
          error: 'Failed to read text file'
        };
      })
    );
  }

  /**
   * Determine file type from filename
   */
  private getFileType(fileName: string): 'pdf' | 'docx' | 'txt' | 'unknown' {
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
        return 'pdf';
      case 'docx':
        return 'docx';
      case 'txt':
        return 'txt';
      default:
        return 'unknown';
    }
  }

  /**
   * Clean up blob URL to prevent memory leaks
   */
  revokeBlobUrl(blobUrl: string): void {
    try {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'revokeBlobUrl');
    }
  }

  /**
   * Format error for logging
   */
  private formatError(error: any): any {
    if (error?.error instanceof ErrorEvent) {
      return { message: error.error.message };
    } else {
      return {
        status: error?.status,
        message: error?.message,
        error: error?.error
      };
    }
  }
}

